import { supabase } from '@/integrations/supabase/client';

const PREVIEW_HOST_SUFFIXES = ['.lovable.app', '.lovableproject.com'];
const TURNSTILE_VERIFY_PATH = '/functions/v1/verify-turnstile';

interface TurnstileResponse {
  success?: boolean;
  code?: string;
  details?: string[];
}

export interface TurnstileVerificationResult {
  success: boolean;
  reason?: string;
}

export function isTurnstileBypassedEnv(): boolean {
  const hostname = window.location.hostname;
  return (
    hostname === 'localhost'
    || hostname === '127.0.0.1'
    || PREVIEW_HOST_SUFFIXES.some((suffix) => hostname.endsWith(suffix))
  );
}

function mapVerificationFailure(response: TurnstileResponse, fallback = 'Security verification failed. Please try again.'): string {
  if (response.code === 'rate_limited') return 'Too many verification attempts. Please wait a minute and try again.';

  const details = response.details ?? [];
  if (details.includes('invalid-input-secret')) return 'Security verification is temporarily unavailable. Please try again shortly.';
  if (details.includes('invalid-input-response') || details.includes('timeout-or-duplicate')) return 'Security check expired. Please complete it again.';
  if (details.includes('invalid-remoteip')) return 'Security check could not be validated from your network. Please retry.';

  return fallback;
}

function shouldFailOpen(response: TurnstileResponse): boolean {
  return ['server_misconfiguration', 'verification_unavailable', 'internal_error'].includes(response.code ?? '');
}

function toVerificationResult(response: TurnstileResponse): TurnstileVerificationResult {
  if (response.success) return { success: true };

  if (shouldFailOpen(response)) {
    console.warn('[Turnstile] verification service unavailable, allowing auth flow to proceed', response);
    return { success: true };
  }

  return {
    success: false,
    reason: mapVerificationFailure(response),
  };
}

async function verifyViaFetch(token: string): Promise<TurnstileResponse | null> {
  try {
    const publishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}${TURNSTILE_VERIFY_PATH}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: publishableKey,
        Authorization: `Bearer ${publishableKey}`,
      },
      body: JSON.stringify({ token }),
    });

    const payload = await response.json().catch(() => null) as TurnstileResponse | null;
    return payload;
  } catch {
    return null;
  }
}

export async function verifyTurnstileToken(token: string | null): Promise<TurnstileVerificationResult> {
  if (isTurnstileBypassedEnv()) return { success: true };
  if (!token) return { success: false, reason: 'Please complete the security check.' };

  let invokeError: unknown = null;

  try {
    const { data, error } = await supabase.functions.invoke('verify-turnstile', {
      body: { token },
    });

    if (!error) {
      return toVerificationResult((data ?? {}) as TurnstileResponse);
    }

    invokeError = error;
  } catch (err) {
    invokeError = err;
  }

  const fallbackResponse = await verifyViaFetch(token);
  if (fallbackResponse) {
    return toVerificationResult(fallbackResponse);
  }

  console.warn('[Turnstile] Both invoke and fetch fallback failed; allowing auth flow to proceed', invokeError);
  return { success: true };
}
